import { ReactNode, useMemo, useEffect, useRef, useState, memo } from 'react';
import classNames from 'classnames';
import { Footer } from '../../footer/Footer';
import { getNavbarSizeClasses } from '../../navbar/constants';
import { BackgroundThemeRevision, PageThemeRevision, NavbarThemeRevision } from 'referral-types';
import { AnimatePresence, m } from 'framer-motion';
import { SubscriptionsContainer } from '../../subscriptions/SubscriptionsContainer';
import { NavbarThemeWrapper } from '../../theme/NavbarThemeWrapper';
import { ButtonType } from '../buttons/ButtonType';
import { BackgroundThemeWrapper } from '../../theme/BackgroundThemeWrapper';
import { usePurchase } from '../../../context/purchase/usePurchase';
import ResponsiveCircle from '../ResponsiveCircle';
import { ThemeBackground } from '../../../context/theme/ThemeConstantsUpdated';
import { Size } from '../../../constants';

const SUBSCRIPTION_ANIMATION_DURATION = 0.7;
const NAVBAR_OFFSET_TRANSITION = { duration: 0.25, ease: 'easeInOut' };

const { mainBackground } = ThemeBackground;

const AppLayout = memo((props: Props) => {
  const {
    className,
    pageThemeRevision,
    children,
    isPreviewing,
    isEditing,
    includeFooter,
    renderSubNav,
    removeBorder,
    navbarThemeRevision,
    backgroundThemeRevision,
    renderCartToggle,
    isLightingLocked,
    anchorId,
    logoOverride,
    pageId,
    isVibrantFooter,
    isNavbarButtonsDisabled,
    pagePublishedDateTime,
    isDebugMode,
    removeNavbarShadow,
    forceNavbarBorder,
  } = props;
  const { isSubscribing } = usePurchase();
  const wasSubscribingRef = useRef<boolean>();
  const [needsLowerZ, setNeedsLowerZ] = useState<boolean>();
  const hasSubNav = renderSubNav != null;
  const navbarSize = navbarThemeRevision?.navbarSize ?? Size.md;
  const { contentTopPadding } = getNavbarSizeClasses(navbarSize);
  const contentTopOffset = useMemo(() => Number(contentTopPadding.match(/\d+/)?.[0] || '0'), [contentTopPadding]);

  // adjust z-index for navbar
  useEffect(() => {
    const wasSubscribing = wasSubscribingRef.current;

    if (wasSubscribing && !isSubscribing) {
      setTimeout(() => setNeedsLowerZ(false), SUBSCRIPTION_ANIMATION_DURATION * 1000);
    } else if (!wasSubscribing && isSubscribing) {
      setNeedsLowerZ(true);
    }

    wasSubscribingRef.current = isSubscribing;
  }, [isSubscribing]);

  const Navbar = useMemo(
    () => (
      <NavbarThemeWrapper
        key={pagePublishedDateTime}
        themeRevision={navbarThemeRevision}
        pageThemeRevision={pageThemeRevision}
        removeBorder={removeBorder}
        renderCartToggle={renderCartToggle}
        isLightingLocked={isLightingLocked}
        needsLowerZ={needsLowerZ}
        logoOverride={logoOverride}
        pageId={pageId}
        isEditing={isEditing}
        isPreviewing={isPreviewing}
        isNavbarButtonsDisabled={isNavbarButtonsDisabled}
        hasSubNav={renderSubNav != null}
        removeShadow={removeNavbarShadow}
        forceBorder={forceNavbarBorder}
      />
    ),
    [
      navbarThemeRevision,
      pageThemeRevision,
      removeBorder,
      renderCartToggle,
      isLightingLocked,
      needsLowerZ,
      logoOverride,
      pageId,
      isEditing,
      isPreviewing,
      isNavbarButtonsDisabled,
      pagePublishedDateTime,
      removeNavbarShadow,
    ]
  );

  const Background = useMemo(
    () => (
      <BackgroundThemeWrapper
        themeRevision={backgroundThemeRevision}
        navbarSize={navbarThemeRevision?.navbarSize}
        topOffset={contentTopOffset}
        mediaOverride={props.backgroundImageOverride}
        mediaMobileOverride={props.backgroundImageMobileOverride}
        pageId={pageId}
        isEditing={isEditing}
        isPreviewing={isPreviewing}
      />
    ),
    [
      backgroundThemeRevision,
      navbarThemeRevision?.navbarSize,
      props.backgroundImageOverride,
      props.backgroundImageMobileOverride,
      pageId,
      isEditing,
      isPreviewing,
    ]
  );

  return (
    <>
      <AnimatePresence key="body_subscription_page">
        {isSubscribing && (
          <m.div
            key="motion_subscription_page"
            initial={{ x: '100%' }}
            animate={{ x: '0%' }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.7, ease: 'easeInOut' }}
            className={classNames(mainBackground, 'fixed z-[55] h-full w-screen')}
          >
            <SubscriptionsContainer />
          </m.div>
        )}
      </AnimatePresence>

      {Navbar}

      {Background}

      <m.main
        id="main-content"
        animate={{
          marginTop: contentTopOffset,
        }}
        transition={NAVBAR_OFFSET_TRANSITION}
        className={classNames(className, 'relative', hasSubNav && !isPreviewing && 'pt-16')}
      >
        <div className={classNames(!isPreviewing && 'relative')}>
          {hasSubNav && (
            <m.div
              animate={{ top: contentTopOffset }}
              transition={NAVBAR_OFFSET_TRANSITION}
              className="fixed z-[51] w-full"
            >
              {renderSubNav()}
            </m.div>
          )}

          {anchorId && <div id={anchorId} />}

          {children}

          {isDebugMode && <ResponsiveCircle className="fixed bottom-0 right-0 z-[100]" />}

          {includeFooter && <Footer isVibrant={isVibrantFooter} />}
        </div>
      </m.main>
    </>
  );
});

type Props = {
  className?: string;
  children: ReactNode;
  includeFooter?: boolean;
  removeBorder?: boolean;
  renderSubNav?: () => ReactNode;
  renderCartToggle?: (buttonType: ButtonType) => ReactNode;
  navbarThemeRevision?: Partial<NavbarThemeRevision>;
  backgroundThemeRevision?: Partial<BackgroundThemeRevision>;
  pageThemeRevision?: Partial<PageThemeRevision>;
  isLightingLocked?: boolean;
  anchorId?: string;
  isPreviewing?: boolean;
  isEditing?: boolean;
  logoOverride?: File | null;
  backgroundImageOverride?: File | null;
  backgroundImageMobileOverride?: File | null;
  pageId?: string;
  pagePublishedDateTime?: string;
  isVibrantFooter?: boolean;
  isNavbarButtonsDisabled?: boolean;
  includeDarkModeToggle?: boolean;
  isDebugMode?: boolean;
  removeNavbarShadow?: boolean;
  forceNavbarBorder?: boolean;
};

export default AppLayout;
